﻿import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { HeroFull } from "@/components/HeroFull";
import { HERO_IMAGE_DEFAULT } from "@/config/images";
import { getCityH1Persianas } from "@/config/city-h1-variants";
import { getFaqsForCity } from "@/config/city-faqs";
import { getPricingForCity, PRICING_TIER_MAP } from "@/config/city-pricing";
import { getConditionsForCity } from "@/config/city-conditions";
import { CITY_TO_REGION, CITY_REGION_CROSS_LINKS } from "@/config/city-to-region";
import { CITY_LABELS } from "@/config/city-neighbors";
import Header from "@/components/feature/Header";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Footer from "@/components/feature/Footer";
import WhatsAppFloating from "@/components/feature/WhatsAppFloating";
import CookieBanner from "@/components/feature/CookieBanner";
import CrossLinksFooter from "@/components/feature/CrossLinksFooter";
import { NeighborCities } from "@/components/feature/NeighborCities";
import SEO, { generateFAQPageSchema } from "@/components/feature/SEO";
import { getPersianasProblemsImage, getProcessImage, getTrustImage } from "@/config/city-images";

export interface CityPagePersianasProps {
  name: string;
  slug: string;
  cp: string;
  poblacion: number;
  zonaGeo: string;
  lat: number;
  lng: number;
  zonas: string[];
  landmarks: string[];
  municipiosLimitrofes: string[];
  festivalLocal: string;
  contextoTipologico: string;
  isCoreZone: boolean;
}

const problemas = [
  { emoji: "🔧", texto: "Mi persiana no sube ni baja", label: "Mecanismo atascado" },
  { emoji: "🪢", texto: "Cinta rota o atascada", label: "Cinta dañada" },
  { emoji: "⚡", texto: "Motor estropeado / no responde", label: "Motor averiado" },
  { emoji: "📐", texto: "Lamas dañadas o caídas", label: "Lamas rotas" },
  { emoji: "🏚️", texto: "Cajón descuadrado o ruidoso", label: "Cajón desajustado" },
  { emoji: "🔒", texto: "Persiana bloqueada en posición", label: "Bloqueo mecánico" },
];

const pasos = [
  { emoji: "📞", titulo: "Contacto rápido", desc: "Llamada o WhatsApp en menos de 20 min" },
  { emoji: "📹", titulo: "Diagnóstico", desc: "Por videollamada WhatsApp o visita técnica gratis" },
  { emoji: "🔧", titulo: "Reparación", desc: "Mismo día o 24h máximo" },
  { emoji: "✅", titulo: "Garantía", desc: "2 años en reparaciones y piezas" },
];

const trustCards = [
  { emoji: "🛡️", titulo: "Seguro de Responsabilidad Civil", desc: "Cobertura total en cada intervención" },
  { emoji: "🕐", titulo: "+10 años · desde 2015", desc: "Experiencia real en toda la provincia" },
  { emoji: "🏆", titulo: "Marcas premium", desc: "Somfy, Cherubini, Becker oficiales" },
  { emoji: "📝", titulo: "Garantía 2 años por escrito", desc: "En piezas y mano de obra" },
];

const VIDEOCALL_IMAGE =
  "https://readdy.ai/api/search-image?query=A%20real%20professional%20roller%20shutter%20repair%20technician%20showing%20a%20broken%20roller%20shutter%20mechanism%20to%20a%20client%20through%20a%20WhatsApp%20video%20call%20on%20a%20smartphone%20screen%2C%20Mediterranean%20home%20interior%20visible%20in%20the%20background%2C%20warm%20natural%20daylight%2C%20authentic%20professional%20service%20photography%2C%20detailed%20tools%20and%20hands%20visible%2C%20shallow%20depth%20of%20field&width=600&height=500&seq=persianas-videocall-real&orientation=landscape&format=webp";

export default function CityPagePersianas({
  name,
  slug,
  cp,
  municipiosLimitrofes,
  zonas,
  landmarks,
  festivalLocal,
  contextoTipologico,
  isCoreZone,
}: CityPagePersianasProps) {
  const [agreed, setAgreed] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleFormSubmit = useFormSubmit(agreed);

  const tier = PRICING_TIER_MAP[slug] || 'tier2';
  const conditions = getConditionsForCity(slug, tier);

  const prices = getPricingForCity(slug);

  const offers = [
    { name: "Cambiar cinta persiana", price: String(prices.cinta), priceCurrency: "EUR" },
    { name: "Reparar motor persiana", price: String(prices.motorReparar), priceCurrency: "EUR" },
    { name: "Sustituir motor", price: String(prices.motorSustituir), priceCurrency: "EUR" },
  ];

  const altTexts = [
    `Técnico reparando persiana eléctrica Somfy en una vivienda de ${name}`,
    `Motor de persiana instalado en piso del centro de ${name}`,
    `Cambio de cinta de persiana en ${zonas?.[0] || name}`,
    `Persiana de aluminio nueva instalada en chalet de ${zonas?.[1] || name}`,
    `Reparación urgente de persiana atascada en vivienda de ${name}`,
  ];

  const intro = `En ${name}, las persianas se enfrentan a condiciones específicas. ${contextoTipologico || 'El clima mediterráneo de la Costa del Sol exige materiales resistentes al salitre marino y a la radiación solar intensa de más de 300 días al año.'} ${landmarks?.length ? `Trabajamos regularmente en viviendas cerca de ${landmarks.slice(0,2).join(' y ')}, ` : ''}desde apartamentos modernos en ${zonas?.[0] || 'el centro'} hasta chalets independientes en ${zonas?.[zonas.length-1] || 'las urbanizaciones'}. ${festivalLocal ? `Durante eventos como ${festivalLocal}, la demanda de reparaciones urgentes aumenta significativamente, por eso garantizamos visita en 24h incluso en temporada alta. ` : ''}Cubrimos todo ${name} con técnico propio, sin intermediarios, ofreciendo presupuesto gratuito, visita en 24 horas y garantía de 2 años en piezas y mano de obra.`;

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const region = CITY_TO_REGION[slug];
  const regionNeighbors = (CITY_REGION_CROSS_LINKS[slug] || [])
    .filter((s) => !!CITY_LABELS[s])
    .slice(0, 4);

  const faqs = getFaqsForCity(slug, name, zonas || []);

  const faqSchema = generateFAQPageSchema(faqs);

  const zonePills = [
    { label: "Málaga", slug: "malaga" },
    { label: "Marbella", slug: "marbella" },
    { label: "Fuengirola", slug: "fuengirola" },
    { label: "Torremolinos", slug: "torremolinos" },
    { label: "Benalmádena", slug: "benalmadena" },
    { label: "Mijas", slug: "mijas" },
    { label: "Estepona", slug: "estepona" },
    { label: "Nerja", slug: "nerja" },
    { label: "Rincón de la Victoria", slug: "rincon-de-la-victoria" },
  ];

  const handleZoneClick = () => {
    const el = document.getElementById("presupuesto");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const waVideoUrl = `https://wa.me/34627213347?text=Hola%2C%20quiero%20un%20videodiagn%C3%B3stico%20de%20mi%20persiana%20en%20${encodeURIComponent(name)}`;
  const waUrl = `https://wa.me/34627213347?text=Hola%2C%20me%20interesa%20info%20sobre%20persianas%20en%20${encodeURIComponent(name)}`;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Persianas ${name} 24h | Reparación e Instalación Pro`}
        description={`Persianas en ${name}: reparación motor, cinta, lamas. Visita 24h. Diagnóstico WhatsApp gratis. ☎ 951 12 07 70`}
        canonical={`/persianas/${slug}/`}
        ogImage={`https://persianas-toldos-malaga.es/og/og-persianas-${slug}.jpg`}
        ogType="website"
        breadcrumbs={
          isCoreZone
            ? [
                { name: "Inicio", path: "/" },
                { name: `Servicios en ${name}`, path: `/zonas/${slug}` },
                { name: "Persianas" },
              ]
            : region
            ? [
                { name: "Inicio", path: "/" },
                { name: region.name, path: `/zonas/${region.slug}` },
                { name: `Persianas en ${name}` },
              ]
            : [
                { name: "Inicio", path: "/" },
                { name: "Persianas", path: "/persianas" },
                { name: name },
              ]
        }
        offers={offers}
      />
      <Header />

      <main id="main-content">
        <Breadcrumbs
          items={
            isCoreZone
              ? [
                  { name: "Inicio", path: "/" },
                  { name: `Servicios en ${name}`, path: `/zonas/${slug}` },
                  { name: "Persianas" },
                ]
              : region
              ? [
                  { name: "Inicio", path: "/" },
                  { name: region.name, path: `/zonas/${region.slug}` },
                  { name: `Persianas en ${name}` },
                ]
              : [
                  { name: "Inicio", path: "/" },
                  { name: "Persianas", path: "/persianas" },
                  { name: name },
                ]
          }
        />
        <HeroFull
          imageSrc={HERO_IMAGE_DEFAULT}
          imageAlt={`Reparación de persianas en ${name} por técnico propio Somfy/Cherubini/Becker`}
          badge="⚡ Diagnóstico GRATIS por WhatsApp · Respuesta 20 min"
          title={getCityH1Persianas(slug, name)}
          subtitle="¿Tu persiana no sube? ¿Cinta rota? ¿Motor estropeado? Lo reparamos hoy mismo. Presupuesto sin compromiso."
          primaryCta={{ href: "tel:+34951120770", label: "Llamar: 951 12 07 70" }}
          secondaryCta={{ href: "https://wa.me/34627213347?text=Hola%2C%20necesito%20diag%20persiana", label: "💬 Diagnóstico WhatsApp" }}
          trustSignals={["+10 años en Málaga", "Garantía 2 años", "Visita 24h"]}
        />

        {/* ===== BLOQUE 1.5 — CONTEXTO LOCAL ===== */}
        <section className="bg-white py-12 md:py-16 border-t border-border">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              {/* Left: text */}
              <div className="w-full lg:w-1/2">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
                  Reparación de persianas adaptada a {name}{zonas?.length >= 3 ? ` y barrios como ${zonas.slice(0,3).join(', ')}` : ''}
                </h2>
                <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed">
                  {intro}
                </p>
              </div>
              {/* Right: image */}
              <motion.div
                className="w-full lg:w-1/2"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <div className="relative rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://storage.readdy-site.link/project_files/cf1847c3-949c-4dd2-b2c0-64bc50d47abe/1363252a-39d0-4526-a457-a7e0a844f48c_malaga.jpg?v=cc6fc93d737a6a21dc06e057cf8cf747"
                    alt={`Reparación de persianas en ${name} por técnico profesional`}
                    className="w-full h-auto object-cover aspect-[4/3]"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ===== BLOQUE 2 — ¿TIENES ESTE PROBLEMA? ===== */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-4">
              ¿Tienes este problema en {name}?
            </h2>
            <p className="font-body text-lg text-text-secondary text-center mb-10 max-w-2xl mx-auto">
              Hacemos clic en el que te pasa y te llevamos al formulario. O llámanos directamente al <a href="tel:+34951120770" className="text-primary font-semibold underline">951 12 07 70</a>.
            </p>
            <div className="max-w-2xl mx-auto mb-10">
              <img
                src="https://storage.readdy-site.link/project_files/cf1847c3-949c-4dd2-b2c0-64bc50d47abe/b4b6f4e4-5660-47b4-bb16-d125af0956b3_persianasmijas.jpg?v=5661e6102e574341592f9b7055054919"
                alt={altTexts[1]}
                className="w-full h-auto rounded-xl object-cover aspect-[16/9]"
                loading="lazy"
                width="600"
                height="338"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {problemas.map((p, i) => (
                <motion.button
                  key={p.label}
                  onClick={scrollToForm}
                  className="bg-white rounded-2xl p-6 text-left border border-border hover:border-primary hover:shadow-md transition-all cursor-pointer group touch-target"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.08 }}
                >
                  <div className="flex items-start gap-4">
                    <span className="text-3xl shrink-0" aria-hidden="true">{p.emoji}</span>
                    <div className="flex-1">
                      <p className="font-body font-semibold text-base text-text-primary group-hover:text-primary transition-colors">
                        {p.texto}
                      </p>
                      <span className="inline-flex items-center gap-1 mt-3 font-body font-medium text-sm text-primary">
                        Sí, esto me pasa <span aria-hidden="true">→</span>
                      </span>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECCIÓN A — 2 COL: PROCESO (imagen IZQ, texto DER) ===== */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* COLUMNA IZQUIERDA — Imagen */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src="https://storage.readdy-site.link/project_files/cf1847c3-949c-4dd2-b2c0-64bc50d47abe/e4ee7dc9-0266-4fcb-9a77-687c79c46cb0_As-te-ayudamos-en-Mlaga.png?v=343c8aa1f136494b41e83a0a185a3926"
                  alt={altTexts[2]}
                  loading="lazy"
                  className="w-full h-auto object-cover rounded-2xl"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                  width="600"
                  height="500"
                />
              </motion.div>
              {/* COLUMNA DERECHA — Texto */}
              <motion.div
                className="flex flex-col gap-6"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <p className="font-body font-bold text-xs tracking-widest uppercase text-primary">NUESTRO PROCESO</p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary leading-tight">
                  Así te ayudamos en {name}
                </h2>
                <ol className="flex flex-col gap-5">
                  {pasos.map((s) => (
                    <li key={s.titulo} className="flex items-start gap-4">
                      <div className="w-10 h-10 shrink-0 bg-accent/40 rounded-full flex items-center justify-center text-xl" aria-hidden="true">
                        {s.emoji}
                      </div>
                      <div>
                        <p className="font-heading font-bold text-base text-text-primary">{s.titulo}</p>
                        <p className="font-body text-sm text-text-secondary leading-relaxed">{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <div>
                  <button
                    onClick={scrollToForm}
                    className="inline-flex items-center gap-2 bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white font-body font-bold text-base px-6 py-3 rounded-pill transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Pedir presupuesto <span aria-hidden="true">→</span>
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ===== BLOQUE 3.5 — CONDICIONES LOCALES ===== */}
        <section className="bg-white py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">{conditions.title}</h2>
            <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed max-w-3xl">{conditions.content}</p>
          </div>
        </section>

        {/* ===== BLOQUE 4 — TARIFAS INDICATIVAS ===== */}
        <section id="tarifas" className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-4">
              Tarifas indicativas en {name}
            </h2>
            <p className="font-body text-lg text-text-secondary text-center mb-10 max-w-2xl mx-auto">
              Precios transparentes. Sin sorpresas. Presupuesto exacto tras diagnóstico gratuito.
            </p>
            <div className="max-w-2xl mx-auto bg-white rounded-3xl border border-border overflow-hidden">
              <div className="divide-y divide-border">
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-surface-alt/50 transition-colors">
                  <span className="font-body font-medium text-text-primary">Cambiar cinta persiana</span>
                  <span className="font-heading font-bold text-lg text-primary">{prices.cinta}€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-surface-alt/50 transition-colors">
                  <span className="font-body font-medium text-text-primary">Reparar motor persiana</span>
                  <span className="font-heading font-bold text-lg text-primary">{prices.motorReparar}€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-surface-alt/50 transition-colors">
                  <span className="font-body font-medium text-text-primary">Sustituir motor</span>
                  <span className="font-heading font-bold text-lg text-primary">{prices.motorSustituir}€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-surface-alt/50 transition-colors">
                  <span className="font-body font-medium text-text-primary">Cambiar lamas (por lama)</span>
                  <span className="font-heading font-bold text-lg text-primary">{prices.lamasPorLama}€</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-surface-alt/50 transition-colors">
                  <span className="font-body font-medium text-text-primary">Desplazamiento</span>
                  <span className="font-heading font-bold text-lg text-primary">{prices.desplazamiento}</span>
                </div>
                <div className="flex items-center justify-between px-6 md:px-8 py-5 hover:bg-surface-alt/50 transition-colors">
                  <span className="font-body font-medium text-text-primary">Revisión + presupuesto</span>
                  <span className="font-heading font-bold text-lg text-green-600">GRATIS</span>
                </div>
              </div>
              <div className="px-6 md:px-8 py-4 bg-surface-alt border-t border-border">
                <p className="font-body text-sm text-text-secondary text-center">
                  {prices.note} Presupuesto exacto sin compromiso tras visita técnica.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SECCIÓN B — 2 COL: VIDEODIAGNÓSTICO (texto IZQ, imagen DER, fondo amarillo) ===== */}
        <section className="py-16 md:py-24" style={{ backgroundColor: "#ECFCCB" }}>
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* COLUMNA IZQUIERDA — Texto */}
              <motion.div
                className="flex flex-col gap-6 md:order-1"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <p className="font-body font-bold text-xs tracking-widest uppercase" style={{ color: "#D97706" }}>
                  📹 NOVEDAD
                </p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary leading-tight">
                  Diagnóstico GRATIS por videollamada WhatsApp
                </h2>
                <p className="font-body text-lg text-text-secondary leading-relaxed">
                  ¿No estás seguro del problema? Muéstranos por videollamada y te asesoramos <strong>GRATIS</strong>. Sin compromiso, sin desplazamiento, en menos de 20 minutos.
                </p>
                <ul className="flex flex-col gap-3">
                  {["Diagnóstico instantáneo", "Presupuesto orientativo al momento", "Ahorra la visita técnica si es sencillo", "Disponible L-V 9-19h, S 9-14h"].map((b) => (
                    <li key={b} className="flex items-center gap-3 font-body text-sm text-text-primary">
                      <span className="w-5 h-5 shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs" aria-hidden="true">✓</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20quiero%20un%20videodiagn%C3%B3stico"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-xl px-8 py-5 rounded-pill transition-colors whitespace-nowrap w-fit"
                >
                  <span aria-hidden="true">📹</span> Iniciar videollamada
                </a>
              </motion.div>
              {/* COLUMNA DERECHA — Imagen */}
              <motion.div
                className="md:order-2"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <img
                  src={VIDEOCALL_IMAGE}
                  alt={`Diagnóstico videollamada WhatsApp persianas ${name}`}
                  loading="lazy"
                  className="w-full h-auto object-cover rounded-2xl"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                  width="600"
                  height="500"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ===== SECCIÓN C — 2 COL: POR QUÉ NOSOTROS (imagen IZQ, texto DER) ===== */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* COLUMNA IZQUIERDA — Imagen */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src={getTrustImage()}
                  alt={altTexts[4]}
                  loading="lazy"
                  className="w-full h-auto object-cover rounded-2xl"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                  width="600"
                  height="500"
                />
              </motion.div>
              {/* COLUMNA DERECHA — Texto */}
              <motion.div
                className="flex flex-col gap-6"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <p className="font-body font-bold text-xs tracking-widest uppercase text-primary">POR QUÉ NOSOTROS</p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary leading-tight">
                  Empresa local desde 2015 · +10 años de experiencia
                </h2>
                <p className="font-body text-lg text-text-secondary leading-relaxed">
                  No somos plataforma intermediaria. Hablas directamente con quien va a reparar tu persiana en {name}. Sin sorpresas, sin sobreprecios.
                </p>
                <ul className="flex flex-col gap-4">
                  {trustCards.map((c) => (
                    <li key={c.titulo} className="flex items-start gap-4">
                      <span className="text-2xl shrink-0" aria-hidden="true">{c.emoji}</span>
                      <div>
                        <p className="font-heading font-bold text-sm text-text-primary">{c.titulo}</p>
                        <p className="font-body text-sm text-text-secondary">{c.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div>
                  <Link
                    to="/sobre-nosotros"
                    className="inline-flex items-center gap-2 bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white font-body font-bold text-base px-6 py-3 rounded-pill transition-colors whitespace-nowrap"
                  >
                    Conócenos <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ===== BLOQUE 8 — COBERTURA DISCRETA ===== */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <p className="font-body text-base text-text-secondary text-center max-w-2xl mx-auto leading-relaxed">
              Trabajamos en todos los barrios de {name}{cp ? ` (CP ${cp})` : ""} y en {municipiosLimitrofes.slice(0, 3).join(", ")}. Sin coste de desplazamiento.
            </p>
            {zonas.length > 0 && (
              <p className="font-body text-sm text-text-secondary text-center mt-2">
                Barrios: {zonas.slice(0, 6).join(" · ")}{zonas.length > 6 ? " · ..." : ""}
              </p>
            )}
          </div>
        </section>

        {/* ===== BLOQUE 9 — FAQ COMERCIAL ===== */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-10">
              Preguntas frecuentes
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((f, i) => (
                <motion.div
                  key={i}
                  className="bg-surface-alt rounded-2xl p-6 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-2">{f.q}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{f.a}</p>
                </motion.div>
              ))}
            </div>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
          </div>
        </section>

        <NeighborCities slug={slug} service="persianas" />

        {/* ===== BLOQUE REGIÓN — CROSS-LINKING TIER 2 ===== */}
        {region && regionNeighbors.length > 0 && (
          <section className="bg-white py-12 md:py-16 border-t border-border">
            <div className="max-w-container mx-auto px-4 md:px-6">
              <h3 className="font-heading font-bold text-xl md:text-2xl text-text-primary mb-3">
                También cubrimos en {region.name}
              </h3>
              <p className="font-body text-sm md:text-base text-text-secondary mb-5 max-w-2xl">
                Trabajamos también en otras poblaciones de {region.name}:
              </p>
              <div className="flex flex-wrap gap-3">
                {regionNeighbors.map((n) => (
                  <Link
                    key={n}
                    to={`/persianas/${n}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-border rounded-pill font-body text-sm text-text-secondary hover:bg-surface-alt hover:text-primary hover:border-primary transition-colors whitespace-nowrap"
                  >
                    <i className="ri-map-pin-line text-xs" aria-hidden="true"></i>
                    {CITY_LABELS[n] || n}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ===== BLOQUE 10 — CTA FINAL + FORM + CROSS-SELL ===== */}
        <section id="presupuesto" className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
              {/* Left: info + CTAs */}
              <div className="w-full lg:w-[40%]">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="font-heading font-extrabold text-3xl md:text-[40px] text-text-primary mb-4 leading-tight">
                    Pídenos presupuesto en {name}
                  </h2>
                  <p className="font-body text-lg text-text-secondary mb-8">
                    Te respondemos en menos de 24h. Desplazamiento incluido en {name}.
                  </p>

                  <div className="flex flex-col gap-3 md:gap-4 mb-6 md:mb-8 w-full">
                    <a
                      href="tel:+34951120770"
                      className="inline-flex items-center justify-center gap-3 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors touch-target"
                    >
                      <span aria-hidden="true">📞</span> 951 12 07 70
                    </a>
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-3 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors touch-target"
                    >
                      <i className="ri-whatsapp-line text-xl" aria-hidden="true"></i> WhatsApp 627 21 33 47
                    </a>
                    <a
                      href={waVideoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-3 bg-white border-2 border-whatsapp text-whatsapp hover:bg-whatsapp hover:text-white font-body font-bold text-base px-6 py-4 rounded-pill transition-colors touch-target"
                    >
                      <span aria-hidden="true">📹</span> Videodiagnóstico gratis
                    </a>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-heading font-bold text-base text-text-primary">También en {name}:</h3>
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to="/toldos"
                        className="inline-flex items-center gap-1.5 bg-surface-alt border border-border hover:border-primary text-text-primary hover:text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill transition-colors"
                      >
                        <i className="ri-sun-line" aria-hidden="true"></i> Toldos
                      </Link>
                      <Link
                        to="/mosquiteras"
                        className="inline-flex items-center gap-1.5 bg-surface-alt border border-border hover:border-primary text-text-primary hover:text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill transition-colors"
                      >
                        <i className="ri-bug-line" aria-hidden="true"></i> Mosquiteras
                      </Link>
                    </div>
                  </div>

                  {isCoreZone && (
                    <div className="mt-6">
                      <Link
                        to={`/zonas/${slug}`}
                        className="inline-flex items-center gap-2 bg-surface-alt border border-border hover:border-primary text-text-primary hover:text-primary font-body font-semibold text-sm px-5 py-3 rounded-pill transition-colors"
                      >
                        Ver todos los servicios en {name} <i className="ri-arrow-right-line" aria-hidden="true"></i>
                      </Link>
                    </div>
                  )}
                </motion.div>
              </div>

              {/* Right: form corto */}
              <motion.div
                className="w-full lg:w-[60%]"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <div className="bg-surface-alt rounded-3xl p-6 md:p-10 border border-border">
                  <form
                    ref={formRef}
                    action="https://formspree.io/f/mdajpjzd"
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    data-readdy-form
                    onSubmit={handleFormSubmit}
                    className="flex flex-col gap-5"
                  >
                    <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                    <input type="hidden" name="ciudad" value={name} />
                    <input type="hidden" name="servicio" value="Persianas" />

                    <div>
                      <label htmlFor={`nombre-${slug}`} className="block font-body font-medium text-sm text-text-primary mb-1.5">
                        Nombre <span className="text-primary">*</span>
                      </label>
                      <input
                        id={`nombre-${slug}`}
                        name="nombre"
                        type="text"
                        required
                        className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div>
                      <label htmlFor={`telefono-${slug}`} className="block font-body font-medium text-sm text-text-primary mb-1.5">
                        Teléfono <span className="text-primary">*</span>
                      </label>
                      <div className="flex">
                        <span className="inline-flex items-center px-4 border border-r-0 border-border rounded-l-xl bg-slate-50 font-body text-sm text-text-secondary shrink-0">
                          +34
                        </span>
                        <input
                          id={`telefono-${slug}`}
                          name="telefono"
                          type="tel"
                          required
                          className="w-full h-12 px-4 border border-border rounded-r-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                          placeholder="612 34 56 78"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor={`mensaje-${slug}`} className="block font-body font-medium text-sm text-text-primary mb-1.5">
                        Mensaje <span className="text-text-secondary">(opcional)</span>
                      </label>
                      <textarea
                        id={`mensaje-${slug}`}
                        name="mensaje"
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                        placeholder={`Cuéntanos qué necesitas en ${name}...`}
                      ></textarea>
                      <p className="mt-1 text-xs text-text-secondary font-body">Máximo 500 caracteres</p>
                    </div>

                    <div className="flex items-start gap-2.5">
                      <input
                        id={`privacidad-${slug}`}
                        name="privacidad"
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-primary shrink-0"
                      />
                      <label htmlFor={`privacidad-${slug}`} className="font-body text-sm text-text-secondary leading-relaxed">
                        He leído y acepto la{" "}
                        <Link to="/politica-privacidad" className="underline text-secondary hover:text-secondary-dark transition-colors">
                          Política de Privacidad
                        </Link>{" "}
                        <span className="text-primary">*</span>
                      </label>
                      <p className="font-body text-xs text-text-secondary/70 mt-1 leading-relaxed">
                        Responsable: Persianas Toldos Málaga. Finalidad: responder a tu consulta y enviarte presupuesto. Legitimación: tu consentimiento. Destinatarios: no se cederán datos a terceros. Derechos: puedes ejercer tus derechos de acceso, rectificación y supresión escribiendo a info@persianas-toldos-malaga.es
                      </p>
                    </div>

                    <button
                      type="submit"
                      className="w-full h-14 bg-text-primary hover:bg-slate-800 text-white font-heading font-bold text-lg rounded-pill transition-colors flex items-center justify-center gap-2 touch-target"
                    >
                      Pedir presupuesto gratis <span aria-hidden="true">→</span>
                    </button>

                    <p className="text-center font-body text-sm text-text-secondary">
                      o llámanos al{" "}
                      <a href="tel:+34951120770" className="font-semibold text-primary hover:text-primary-dark underline">
                        951 12 07 70
                      </a>
                    </p>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ZONAS PILLS */}
        <section className="bg-surface-alt py-12 md:py-16 border-t border-border">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h3 className="font-heading font-bold text-lg text-text-primary text-center mb-6">
              Persianas en toda la Costa del Sol
            </h3>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {zonePills.map((z) => {
                const active = z.slug === slug || (z.slug === "malaga" && slug === "malaga");
                return (
                  <Link
                    key={z.slug}
                    to={`/persianas/${z.slug}`}
                    onClick={() => !active && handleZoneClick()}
                    className={`inline-flex items-center font-body text-sm px-4 py-2 rounded-pill transition-colors whitespace-nowrap ${
                      active
                        ? "bg-primary text-white font-semibold"
                        : "bg-white text-text-secondary hover:bg-primary hover:text-white border border-border hover:border-primary"
                    }`}
                  >
                    {z.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <CrossLinksFooter serviceSlug="persianas" cityName={name} currentSlug={slug} />
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}